/**
 * Florame x Shopify Inbox
 * - Il launcher Shopify resta sempre nascosto.
 * - Soltanto un comando Florame esplicito autorizza la chat a diventare visibile.
 * - Lo stato open ripristinato da Shopify dopo una navigazione viene chiuso.
 */
(function () {
  'use strict';

  const HOST_SELECTOR = '#ShopifyChat, #shopify-chat, inbox-online-store-chat, shopify-chat, shopify-agent';
  const TOGGLE_SELECTOR = 'button[data-spec="toggle-button"], .chat-toggle, button[part~="activator"], .activator';
  const CLOSE_SELECTOR = '[aria-label="Chiudi chat"], [aria-label="Close chat"]';
  const GUARD_STYLE_ID = 'florame-hide-shopify-inbox-bubble';
  const LEGACY_DOCK_STYLE_ID = 'florame-shopify-inbox-dock-v1';
  const GUARDED_ATTRIBUTE = 'data-florame-inbox-guarded';
  const AUTHORIZED_ATTRIBUTE = 'data-florame-inbox-authorized';
  const observedRoots = new WeakSet();
  const observedHosts = new WeakSet();
  let documentObserver = null;
  let retryTimer = 0;
  let retryCount = 0;
  let lifecycleState = 'closed';
  let lifecycleEpoch = 0;
  let openingPromise = null;

  const guardCss = `
    .activator-nudge,
    button[data-spec="toggle-button"],
    .chat-toggle,
    button[part~="activator"],
    .activator,
    .chat-notification {
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }

    .activator-nudge,
    .chat-toggle,
    button[part~="activator"],
    .activator {
      width: 1px !important;
      min-width: 1px !important;
      max-width: 1px !important;
      height: 1px !important;
      min-height: 1px !important;
      max-height: 1px !important;
      padding: 0 !important;
      margin: 0 !important;
      border: 0 !important;
      overflow: hidden !important;
      box-shadow: none !important;
    }
  `;

  function hosts() {
    return Array.prototype.slice.call(document.querySelectorAll(HOST_SELECTOR));
  }

  function getChatEl() {
    return document.getElementById('ShopifyChat')
      || document.getElementById('shopify-chat')
      || document.querySelector('inbox-online-store-chat, shopify-chat, shopify-agent');
  }

  function deepFind(root, selector) {
    if (!root) return null;
    const direct = root.querySelector?.(selector);
    if (direct) return direct;
    const nodes = root.querySelectorAll?.('*') || [];
    for (let index = 0; index < nodes.length; index += 1) {
      if (!nodes[index].shadowRoot) continue;
      const nested = deepFind(nodes[index].shadowRoot, selector);
      if (nested) return nested;
    }
    return null;
  }

  function deepFindVisible(root, selector) {
    if (!root) return null;
    const direct = root.querySelectorAll?.(selector) || [];
    for (let index = 0; index < direct.length; index += 1) {
      const rect = direct[index].getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return direct[index];
    }
    const nodes = root.querySelectorAll?.('*') || [];
    for (let index = 0; index < nodes.length; index += 1) {
      if (!nodes[index].shadowRoot) continue;
      const nested = deepFindVisible(nodes[index].shadowRoot, selector);
      if (nested) return nested;
    }
    return null;
  }

  function getToggle(host) {
    const currentHost = host || getChatEl();
    return currentHost && currentHost.shadowRoot ? deepFind(currentHost.shadowRoot, TOGGLE_SELECTOR) : null;
  }

  function hostIsOpen(host) {
    if (!host) return false;
    if (host.open === true || host.hasAttribute?.('open')) return true;
    return Boolean(host.shadowRoot && deepFindVisible(host.shadowRoot, CLOSE_SELECTOR));
  }

  function isHostAuthorized(host) {
    return Boolean(host && host.hasAttribute?.(AUTHORIZED_ATTRIBUTE));
  }

  function closeRuntimeHost(host) {
    if (!host || !hostIsOpen(host)) return true;
    try {
      if (typeof host.close === 'function') {
        host.close();
        return true;
      }
      const closeButton = host.shadowRoot ? deepFindVisible(host.shadowRoot, CLOSE_SELECTOR) : null;
      if (closeButton) {
        closeButton.click();
        return true;
      }
      const toggle = getToggle(host);
      if (toggle) {
        toggle.click();
        return true;
      }
    } catch (_) {}
    return false;
  }

  function revokeAuthorization(host, closeHost) {
    if (!host) return;
    host.removeAttribute?.(AUTHORIZED_ATTRIBUTE);
    if (closeHost) closeRuntimeHost(host);
  }

  function syncHostState(host) {
    if (!host) return;
    const open = hostIsOpen(host);
    const authorized = isHostAuthorized(host);
    if (open && !authorized) {
      closeRuntimeHost(host);
      lifecycleState = 'closed';
      return;
    }
    if (!open && authorized && lifecycleState !== 'opening') {
      revokeAuthorization(host, false);
      lifecycleState = 'closed';
      return;
    }
    if (open && authorized) lifecycleState = 'open';
  }

  function observeRoot(root) {
    if (!root || observedRoots.has(root)) return;
    const observer = new MutationObserver(function () {
      ensureLauncherHidden();
    });
    observer.observe(root, { childList: true, subtree: true });
    observedRoots.add(root);
  }

  function observeHost(host) {
    if (!host || observedHosts.has(host)) return;
    const observer = new MutationObserver(function () {
      ensureLauncherHidden();
    });
    observer.observe(host, { attributes: true, attributeFilter: ['open'], childList: true });
    observedHosts.add(host);
  }

  function guardShadowTree(root) {
    if (!root) return;
    root.getElementById?.(LEGACY_DOCK_STYLE_ID)?.remove();
    if (!root.getElementById?.(GUARD_STYLE_ID)) {
      const style = document.createElement('style');
      style.id = GUARD_STYLE_ID;
      style.textContent = guardCss;
      if (document.documentElement.getAttribute('data-florame-ui') === 'unified' || document.documentElement.hasAttribute('data-florame-marketplace-home')) {
        // Extend the existing shadow-root stylesheet; no new observers or chat behavior.
        style.textContent += `
          :host { font-family: var(--ui-font, FlorameInter, system-ui) !important; }
          .panel { border: 1px solid #eee4e9 !important; border-radius: 28px !important; background: #fff9fb !important; }
          .sign-in-button { background: #ff4d75 !important; color: #421323 !important; border: 0 !important; border-radius: 14px !important; min-height: 44px !important; padding-inline: 18px !important; font: 600 14px var(--ui-font, FlorameInter, system-ui) !important; }
        `;
      }
      root.appendChild(style);
    }
    observeRoot(root);
    Array.prototype.forEach.call(root.querySelectorAll('*'), function (node) {
      if (node.shadowRoot) guardShadowTree(node.shadowRoot);
    });
  }

  function guardHost(host) {
    if (!host || !host.shadowRoot) return false;
    if (host.hasAttribute('data-florame-mobile-nav-dock')) host.removeAttribute('data-florame-mobile-nav-dock');
    guardShadowTree(host.shadowRoot);
    observeHost(host);
    if (!host.hasAttribute(GUARDED_ATTRIBUTE)) host.setAttribute(GUARDED_ATTRIBUTE, '');
    syncHostState(host);
    return true;
  }

  function startRetryBurst() {
    if (retryTimer) return;
    retryCount = 0;
    retryTimer = window.setInterval(function () {
      retryCount += 1;
      const guarded = ensureLauncherHidden();
      if (guarded || retryCount >= 40) {
        window.clearInterval(retryTimer);
        retryTimer = 0;
      }
    }, 150);
  }

  function ensureLauncherHidden() {
    const currentHosts = hosts();
    let guarded = false;
    currentHosts.forEach(function (host) {
      guarded = guardHost(host) || guarded;
    });
    if (currentHosts.length && !guarded) startRetryBurst();
    return guarded;
  }

  function waitForToggle() {
    return new Promise(function (resolve) {
      let tries = 0;
      function check() {
        tries += 1;
        ensureLauncherHidden();
        const host = getChatEl();
        const opener = host && typeof host.show === 'function' ? host : getToggle(host);
        if (opener) { resolve(opener); return; }
        if (tries >= 60) { resolve(null); return; }
        window.setTimeout(check, 100);
      }
      check();
    });
  }

  function waitForAuthorizedOpen(host, epoch) {
    return new Promise(function (resolve) {
      let tries = 0;
      function check() {
        tries += 1;
        ensureLauncherHidden();
        if (epoch !== lifecycleEpoch) { resolve(false); return; }
        if (isHostAuthorized(host) && hostIsOpen(host)) { resolve(true); return; }
        if (tries >= 60) { resolve(false); return; }
        window.setTimeout(check, 100);
      }
      check();
    });
  }

  function reinforceAfterTransition() {
    [0, 80, 220, 500, 1000, 1800, 3000].forEach(function (delay) {
      window.setTimeout(ensureLauncherHidden, delay);
    });
  }

  function isChatOpen() {
    const host = getChatEl();
    return Boolean(isHostAuthorized(host) && hostIsOpen(host));
  }

  function openChat(event) {
    try { event?.preventDefault?.(); event?.stopPropagation?.(); } catch (_) {}
    document.getElementById('profileOptions')?.classList.remove('show');
    if (isChatOpen()) return Promise.resolve(true);
    if (openingPromise) return openingPromise;

    lifecycleState = 'opening';
    const epoch = ++lifecycleEpoch;
    openingPromise = waitForToggle().then(function (opener) {
      if (!opener || epoch !== lifecycleEpoch) return false;
      const host = typeof opener.show === 'function' ? opener : getChatEl();
      if (!host) return false;

      host.setAttribute(AUTHORIZED_ATTRIBUTE, '');
      ensureLauncherHidden();
      try {
        if (!hostIsOpen(host)) {
          if (typeof host.show === 'function') host.show();
          else opener.click();
        }
      } catch (_) {
        revokeAuthorization(host, true);
        lifecycleState = 'closed';
        return false;
      }

      reinforceAfterTransition();
      return waitForAuthorizedOpen(host, epoch).then(function (opened) {
        if (opened) {
          lifecycleState = 'open';
          return true;
        }
        revokeAuthorization(host, true);
        lifecycleState = 'closed';
        return false;
      });
    }).finally(function () {
      openingPromise = null;
      if (lifecycleState === 'opening') lifecycleState = 'closed';
    });
    return openingPromise;
  }

  function resetLifecycle() {
    lifecycleEpoch += 1;
    lifecycleState = 'closed';
    openingPromise = null;
    hosts().forEach(function (host) {
      revokeAuthorization(host, true);
    });
  }

  function start() {
    if (!documentObserver) {
      documentObserver = new MutationObserver(function (mutations) {
        const inboxChanged = mutations.some(function (mutation) {
          return Array.prototype.some.call(mutation.addedNodes || [], function (node) {
            return node.nodeType === 1 && (node.matches?.(HOST_SELECTOR) || node.querySelector?.(HOST_SELECTOR));
          });
        });
        if (inboxChanged) {
          ensureLauncherHidden();
          startRetryBurst();
        }
      });
      documentObserver.observe(document.documentElement, { childList: true, subtree: true });
      window.addEventListener('pagehide', resetLifecycle);
      window.addEventListener('pageshow', function () {
        resetLifecycle();
        ensureLauncherHidden();
        startRetryBurst();
      });
    }
    ensureLauncherHidden();
    startRetryBurst();
  }

  window.FlorameShopifyInbox = Object.assign({}, window.FlorameShopifyInbox || {}, {
    version: '3.0.0',
    ensureHidden: ensureLauncherHidden,
    getToggle: getToggle,
    isOpen: isChatOpen,
    waitForToggle: waitForToggle,
    open: openChat,
    close: resetLifecycle
  });
  window.ensureFlorameShopifyInboxHidden = ensureLauncherHidden;
  window.openShopifyInboxFromMenu = openChat;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
